<template>
  <div class="w-full">
    <div v-if="!readOnly" class="flex flex-col gap-2">
      <hit-select
        :label-fields="['designation']"
        :value="selectedActivities"
        :options="filteredActivityOptions"
        :label="label"
        :placeholder="placeholder"
        :options-to-exclude="deactivatedActivities"
        multiple
        @remove="(activityId) => $emit('remove', activityId)"
        @change="onActivityChange"
      />

      <!-- Custom Activity Section -->
      <div class="w-full">
        <!-- Trigger Text (when not editing) -->
        <button
          v-if="!showCustomActivityInput"
          class="w-full py-1 px-2 bg-transparent text-gray-500 text-sm font-medium cursor-pointer transition-all duration-150 text-left focus:outline-none hover:underline hover:text-clickable"
          type="button"
          @click="startAddingCustomActivity"
        >
          <span>{{ t('hit-components.common.add-custom-activity') }}</span>
        </button>

        <!-- Input Form (when editing) -->
        <div v-else class="flex gap-2">
          <input
            ref="customActivityInput"
            v-model="customActivityText"
            type="text"
            class="flex-1 border border-gray-200 bg-white text-gray-900 rounded h-10 px-3 text-sm transition-all duration-150 focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent focus:ring-opacity-20 placeholder:text-gray-400"
            :class="{
              'border-red-500 focus:border-red-500 focus:ring-red-500 focus:ring-opacity-20':
                customActivityError,
            }"
            :placeholder="t('hit-components.common.new-custom-activity')"
            maxlength="100"
            @keydown.enter="addCustomActivity"
            @keydown.escape="cancelAddingCustomActivity"
            @input="validateCustomActivity"
          />
          <hit-button
            prefix-icon="add"
            color="accent"
            :disabled="!canAddCustomActivity"
            @click="addCustomActivity"
          />
          <hit-button prefix-icon="clear" @click="cancelAddingCustomActivity" />
        </div>

        <!-- Error message if any -->
        <div v-if="showCustomActivityInput && customActivityError" class="mt-1">
          <div class="text-red-600 text-xs">
            {{ customActivityError }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex flex-row gap-1 flex-nowrap overflow-x-auto overflow-y-hidden"
    >
      <hit-entity-list :entities-to-display="modelValue" />
    </div>
  </div>
</template>

<script>
import HitEntityList from './HitEntityList.vue';
import HitSelect from '../form/input/HitSelect.vue';
import HitButton from '../button/HitButton.vue';
import axios from 'axios';
import {Base64} from 'js-base64';
import {useI18n} from 'vue-i18n';

export default {
  name: 'ActivityList',
  components: {HitSelect, HitEntityList, HitButton},
  props: {
    /**
     * List of selected activity objects (v-model)
     */
    modelValue: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Flag to make the activity list read only
     */
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The label to display above the component
     */
    label: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The placeholder text for the select input
     */
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The language to use for activity designations
     */
    language: {
      type: String,
      required: false,
      default: 'en_gb',
    },

    adminApiBaseUrl: {
      type: String,
      required: false,
      default: process.env.VUE_APP_ADMIN_API_BASE_URL,
    },
  },

  setup() {
    const {t} = useI18n();
    return {t};
  },

  data() {
    return {
      activityOptions: [], // Formatted options for select
      customActivityText: '', // Text for custom activity
      customActivityError: '', // Error message for custom activity
      customActivities: [], // Array to store custom activities
      OTHER_ACTIVITY_ID: '29594a5a-4f3b-472f-98d3-703364a99927', // Fixed ID for "Autre activité"
      showCustomActivityInput: false, // Flag to show custom activity input
    };
  },

  computed: {
    /**
     * Array of UUIDs with the selected activities (from modelValue)
     */
    selectedActivities() {
      return (this.modelValue || []).map((activity) => activity.id);
    },
    /**
     * Filter out "Autre activité" from the options
     */
    filteredActivityOptions() {
      return this.mergedActivity.filter(
        (activity) => activity.id !== this.OTHER_ACTIVITY_ID,
      );
    },
    /**
     * Merge loaded options with already selected ones (to handle deactivated ones)
     */
    mergedActivity() {
      const combinedActivityObjects = [
        ...this.activityOptions,
        ...this.customActivities,
        ...(this.modelValue || []),
      ].filter((t) => t?.designation);
      return combinedActivityObjects.filter(
        (obj, index, self) =>
          index ===
          self.findIndex(
            (t) => t.id === obj.id && t.designation === obj.designation,
          ),
      );
    },
    /**
     * Selected activities that are no longer in the options (deactivated)
     */
    deactivatedActivities() {
      return (this.modelValue || []).filter(
        (obj) =>
          !this.activityOptions.some(
            (activity) =>
              activity.id === obj.id &&
              activity.designation === obj.designation,
          ) &&
          !this.customActivities.some(
            (activity) =>
              activity.id === obj.id &&
              activity.designation === obj.designation,
          ),
      );
    },
    /**
     * Check if custom activity can be added
     */
    canAddCustomActivity() {
      return (
        this.customActivityText.trim().length >= 3 &&
        this.customActivityText.trim().length <= 100 &&
        !this.customActivityError
      );
    },
  },

  methods: {
    /**
     * Load activities from the API
     */
    async loadActivities() {
      const token = this.generateAdminApiBase64Token();
      const config = {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Basic ${token}`,
        },
      };
      try {
        const response = await axios.get(
          `${this.adminApiBaseUrl}activity/list`,
          config,
        );
        this.activityOptions = response.data.map((activity) => {
          const designation =
            activity[`designation_${this.language}`] ||
            activity.designation_en_gb;
          return {
            id: activity.id,
            label: designation,
            designation: designation,
          };
        });
      } catch (error) {
        console.error('Error during activities loading', error);
      }
    },
    /**
     * Handle selection change
     */
    onActivityChange(value) {
      // Normal selection change (no special handling needed since "Autre activité" is filtered out)
      const allActivities = [
        ...this.activityOptions,
        ...this.customActivities,
        ...(this.modelValue || []),
      ];
      const selectedObjects = value
        .map((id) => allActivities.find((a) => a.id === id))
        .filter(Boolean);

      this.$emit('update:modelValue', selectedObjects);
      this.$emit('change', selectedObjects);
    },
    /**
     * Validate custom activity input
     */
    validateCustomActivity() {
      const text = this.customActivityText.trim();

      if (text.length === 0) {
        this.customActivityError = '';
        return;
      }

      if (text.length < 3) {
        this.customActivityError =
          "L'activité doit contenir au moins 3 caractères";
        return;
      }

      if (text.length > 100) {
        this.customActivityError =
          "L'activité ne peut pas dépasser 100 caractères";
        return;
      }

      // Check if activity already exists
      const allActivities = [...this.activityOptions, ...this.customActivities];
      const exists = allActivities.some(
        (activity) => activity.designation.toLowerCase() === text.toLowerCase(),
      );

      if (exists) {
        this.customActivityError = 'Cette activité existe déjà';
        return;
      }

      this.customActivityError = '';
    },
    /**
     * Add custom activity
     */
    addCustomActivity() {
      if (!this.canAddCustomActivity) {
        return;
      }

      const customActivity = {
        id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        label: this.customActivityText.trim(),
        designation: this.customActivityText.trim(),
        isCustom: true,
      };

      // Add to custom activities list
      this.customActivities.push(customActivity);

      // Add to selected activities
      const currentSelected = [...(this.modelValue || [])];
      currentSelected.push(customActivity);

      this.$emit('update:modelValue', currentSelected);
      this.$emit('change', currentSelected);

      // Reset input and return to text
      this.customActivityText = '';
      this.customActivityError = '';
      this.showCustomActivityInput = false;
    },
    /**
     * Generate authentication token
     */
    generateAdminApiBase64Token() {
      const concatCredentials = `${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_USERNAME}:${process.env.VUE_APP_ADMIN_API_BASIC_AUTH_PASSWORD}`;
      return Base64.encode(concatCredentials);
    },
    /**
     * Start adding custom activity
     */
    startAddingCustomActivity() {
      this.showCustomActivityInput = true;
    },
    /**
     * Cancel adding custom activity
     */
    cancelAddingCustomActivity() {
      this.showCustomActivityInput = false;
    },
  },

  watch: {
    language: {
      handler(_newLanguage) {
        if (!this.readOnly) {
          this.loadActivities();
        }
      },
      immediate: true,
    },
  },

  mounted() {
    if (!this.readOnly) {
      this.loadActivities();
    }
  },

  emits: ['change', 'update:modelValue', 'remove'],
};
</script>
